import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";

import type {
  CreateMarketViewRequest,
  MarketView,
  MarketViewListResponse,
  MarketViewScope,
  UpdateMarketViewRequest,
} from "@/types/market-views";

/**
 * The network layer for Market Intelligence saved views — the chart
 * counterpart to `useSavedViews`.
 *
 * Keyed by `(scope, hub)`, because that is the partition the server lists by:
 * a tenor set saved for PJM West is not a statement about Waha. Two hubs open
 * in two tabs therefore hold two independent caches rather than fighting over
 * one.
 */
const KEY = (scope: MarketViewScope, hubCode: string) =>
  ["market-views", scope, hubCode] as const;

/** Every view the reader saved — the "My views" panel's list. */
const MINE_KEY = ["market-views", "mine"] as const;

function qs(scope: MarketViewScope, hubCode: string): string {
  return new URLSearchParams({ scope, hub_code: hubCode }).toString();
}

export function useMarketViews(scope: MarketViewScope, hubCode: string) {
  const qc = useQueryClient();
  // The panel lists every view, so a save, rename or delete on any surface
  // refreshes it too.
  // Returned, so a mutation's `onSuccess` still waits for the surface's own
  // list to refetch before the caller's await resolves.
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: KEY(scope, hubCode) }),
      qc.invalidateQueries({ queryKey: MINE_KEY }),
    ]);

  const query = useQuery({
    queryKey: KEY(scope, hubCode),
    queryFn: () =>
      api.get<MarketViewListResponse>(`/api/market-views?${qs(scope, hubCode)}`),
    // A hub code is required to list; the surfaces always have one, but a
    // guard here beats a 400 in the console during a route transition.
    enabled: hubCode.length > 0,
  });

  const save = useMutation({
    mutationFn: (body: CreateMarketViewRequest) =>
      api.post<MarketView>("/api/market-views", body),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateMarketViewRequest }) =>
      api.patch<MarketView>(`/api/market-views/${id}`, body),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/market-views/${id}`),
    onSuccess: invalidate,
  });
  const setDefaultUser = useMutation({
    mutationFn: (id: string) =>
      api.post<void>(`/api/market-views/${id}/set-default?${qs(scope, hubCode)}`, {}),
    onSuccess: invalidate,
  });
  const setDefaultSystem = useMutation({
    mutationFn: (systemId: string) =>
      api.post<void>(
        `/api/market-views/system/${encodeURIComponent(systemId)}/set-default?${qs(
          scope,
          hubCode,
        )}`,
        {},
      ),
    onSuccess: invalidate,
  });
  const clearDefault = useMutation({
    mutationFn: () => api.del<void>(`/api/market-views/default?${qs(scope, hubCode)}`),
    onSuccess: invalidate,
  });

  const isDefault = (v: MarketView): boolean => {
    const d = query.data?.default;
    if (!d) return false;
    // `kind` disambiguates: a user view's id is a UUID and a system view's is
    // a `system:market:…` string, so they cannot collide — but comparing the
    // kind too keeps that from being load-bearing.
    if (d.kind === "user" && !v.is_system) return d.view_id === v.id;
    if (d.kind === "system" && v.is_system) return d.view_id === v.id;
    return false;
  };

  const togglePin = async (v: MarketView) => {
    if (isDefault(v)) await clearDefault.mutateAsync();
    else if (v.is_system) await setDefaultSystem.mutateAsync(v.id);
    else await setDefaultUser.mutateAsync(v.id);
  };

  const isPinning =
    setDefaultUser.isPending || setDefaultSystem.isPending || clearDefault.isPending;

  return {
    views: (query.data?.views ?? []) as MarketView[],
    systemViews: (query.data?.system_views ?? []) as MarketView[],
    default: query.data?.default,
    isLoading: query.isLoading,
    save,
    update,
    remove,
    isDefault,
    togglePin,
    isPinning,
  };
}

/**
 * Every view the reader has saved, across every point and chart, most
 * recently changed first — what the "My views" panel lists. User views only:
 * a shipped default is the same on every point, so it says nothing about where
 * to go. Fetched only while the panel is open.
 */
export function useMyMarketViews(enabled: boolean) {
  return useQuery({
    queryKey: MINE_KEY,
    queryFn: () => api.get<MarketView[]>("/api/market-views/mine"),
    enabled,
  });
}
